/**
 * THE RESOLVER FOR EVERY INTENT IN FLIGHT — APP-SCOPED, BECAUSE THE TOAST IT RESOLVES IS APP-SCOPED.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * THE DEFECT THIS EXISTS TO CLOSE
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * A submitted swap raised a STICKY (`ttlMs: null`) "Submitted — confirming" toast from `TradePanel`, and the
 * only thing that could ever replace it was a poll living INSIDE that same panel. `Toasts.tsx` says in its own
 * comment that the host is mounted by the shell "so a toast survives a route change — an order submitted on a
 * token page must still be able to report its outcome after the user navigates away". The toast survived. The
 * POLLER did not: it was a `useEffect` in the panel, so navigating away, closing the token page or reloading
 * aborted the only thing that could resolve the notice, and the spinner span the rest of the session. Reported
 * as "it spins forever and never settles, but solscan already shows it done", which is exactly that shape.
 *
 * Two more holes in the same place, both of which produced a permanent spinner WITHOUT navigating anywhere:
 *   · the 60 s cap set the panel's status line and left the toast pending, forever;
 *   · a session expiry mid-poll did the same.
 *
 * So the rule is: **the thing that raises a sticky notice must be the thing that owns its resolution, and it
 * must live at least as long as the notice does.** That is this module. It is not a component, it holds the
 * watch set outside React, and it persists across a reload.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * ONE READ SERVES EVERY WATCH, AND IT IS OUR OWN ROW — NOT A VENDOR CALL
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * `GET /user/v1/intents` returns this wallet's whole intent log, so N intents in flight cost ONE request per
 * tick, not N. It is a GET with **no `cadence` row** in `USER_ROUTES` — verified, not assumed: the three
 * cadenced routes are `/quote`, `/swap/build` and `/deposit/build`, the three that make us call Jupiter from
 * a body the client controls. This one reads Postgres. Its only bound is the per-client bucket (5 rps,
 * burst 30), against which one read per 2 s is nothing.
 *
 * **THE INTERVAL IS 2 s AND THE BOUND IS 60 s** — `design.md` §"confirm" ratified those numbers and the
 * mechanism behind them has since made the first tick nearly free: `/submit`'s swap arm awaits
 * `intents.advance(id, "confirmed" | "failed")` BEFORE it answers, so by the time the submit response reaches
 * the browser the verdict is already durable. The first read therefore happens IMMEDIATELY, not after a
 * 2 s wait, and normally ends the watch in one round trip. The 2 s cadence is for the case the immediate read
 * is for: a submit that never answered (`SwapOutcome.kind === "unknown"`), where the server may still be
 * mid-request. A Solana confirm is ~400 ms/slot and the answer is a database row, so sub-second polling buys
 * nothing and costs requests.
 *
 * **60 s IS THE BOUND AND IT IS DERIVED, NOT PICKED.** A swap row is made terminal INSIDE the submit request.
 * A row still non-terminal a minute later therefore means our server never got to write a verdict at all —
 * and no amount of further reading of this route can produce one. The recoveries past that point are the
 * server's 15-minute stuck-intent sweep and the activity list on the wallet page, both of which the copy
 * names.
 *
 * ⚠️ **THE BOUND PRODUCES `unknown`, NEVER `failed`, AND THE ORDER OF THE TWO CHECKS IS WHAT GUARANTEES IT.**
 * `verdictFor` tests for a terminal ROW first and the clock second, so a verdict that arrives on the same tick
 * the bound expires still wins. Telling someone their money did not move when it did is the worst output this
 * file has, and it is the one an "if (expired) failed" would produce for every slow-but-successful trade.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ `confirmed` HERE MEANS SOLANA'S CONFIRMED TIER. IT IS NOT FINALITY, AND WE DO NOT HAVE FINALITY.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * `IntentStatus` is `built → submitted → confirmed | failed | abandoned` and **nothing anywhere advances a row
 * past `confirmed`** — there is no `finalized` state on the wire, in the server's machine, or in the vendor
 * response it is written from. `/submit`'s swap arm writes `confirmed` from Jupiter's `status === "Success"`,
 * which is a confirmed-tier landing, and 0.3–2.0 % of confirmed slots are abandoned (~50–100 reverts/hour
 * chain-wide since SIMD-0286). So the success copy says "confirmed" and links the signature, and it
 * deliberately does not say "final", "settled" or "complete". Distinguishing finality would need a
 * `finalized` transition on the intent row; until it exists this is the strongest true statement available,
 * and overstating it is a claim we cannot retract once the user has closed the tab.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT IS DELIBERATELY NOT WATCHED
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * **A TRANSFER.** `/submit`'s transfer arm advances its row to `submitted` and returns; the CHAIN executes, so
 * there is no synchronous verdict to render.
 *
 * ⚠️ THE REASON THIS IS NOT WATCHED HAS CHANGED, AND THE OLD ONE IS NO LONGER TRUE. It used to be that
 * "nothing ever confirms a withdrawal" — a poll could not produce a verdict at any length, and at 15 minutes
 * the stuck sweep would call a landed transfer `abandoned`. `confirm.ts` closed that: it resolves every
 * `submitted` row carrying a signature, kind-agnostically, reading at FINALIZED, and it runs before the sweep
 * in the same tick precisely so a landed transfer is never abandoned. So a verdict now DOES arrive.
 *
 * What is still true is the TIMING: finality is ~12 s and the confirmer runs on its own cadence, so a poll
 * would spin through several ticks before it could say anything. That is a product judgement about a spinner,
 * not the impossibility it used to be — worth revisiting deliberately rather than inheriting.
 *
 * **A RESTING ORDER.** `/order/create` answers with the created order or it does not; `placeOrder` returns
 * `created` / `unresolved` synchronously and `OrderTicket` renders that union inline. There is no pending
 * notice on that path and therefore nothing to resolve.
 */

import type { IntentRowWire, IntentsResponse } from "@kinetiq/wire";
import { isApiError } from "../api/errors.ts";
import * as api from "../api/trade.ts";
import { isSessionExpiry, onUnauthorized } from "../auth/session.ts";
import { nowMs } from "../lib/time.ts";
import { type ReadableStore, createStore } from "../stores/store.ts";
import { type Toast, pushToast } from "../stores/toasts.ts";

/** One read per tick, for every watch at once. See the header for why this number and not a faster one. */
export const WATCH_POLL_MS = 2_000;
/** Past this a watch becomes `unknown` — NEVER `failed`. See the header for why 60 s is derived. */
export const WATCH_BOUND_MS = 60_000;
/** A resolved notice is read, not lived with. Long enough to notice and click through. */
const RESOLVED_TTL_MS = 8_000;
/** The bound's notice gets longer, because it asks the reader to go somewhere else. It is still BOUNDED: a
 *  sticky "we do not know" is the same defect as a sticky "confirming" wearing a calmer face. */
const UNKNOWN_TTL_MS = 30_000;
/** The most watches we will carry across a reload. A trader with more than this in flight has a durable
 *  record on the wallet page; an unbounded list in `sessionStorage` is not a feature. */
const MAX_WATCHES = 8;
/** How many resolved verdicts stay readable in the view store. A panel reads its own verdict once and then
 *  clears its request id, so this only has to outlive one render. */
const MAX_VIEWS = 20;

const STORAGE_KEY = "kinetiq.v3.watch";

export const BOUND_COPY =
  "Still confirming after a minute — we have stopped checking. It may still land; your wallet page lists what happened.";
export const SIGNED_OUT_COPY =
  "You were signed out while this was confirming. The chain does not care — sign in and your wallet page lists what happened.";
export const FAILED_COPY = "This trade failed on chain.";
export const ABANDONED_COPY = "This trade was never completed.";

export interface WatchInit {
  /** THE INTENT ROW ID. For a swap it is the vendor request id that `/swap/build` returned and that
   *  `intents.record` keyed the row on — the same string on both sides, by construction. */
  id: string;
  /** Known at submit time for a swap that answered; `null` for one that did not. It is what puts a solscan
   *  link on the notice IMMEDIATELY rather than only at the end — the user in the report went to solscan
   *  themselves, which is the tell that we were holding the link and not showing it. */
  signature: string | null;
  /** What the sticky notice says while it is in flight. */
  pending: string;
  /** What a CONFIRMED row says. See the header: this word may not be upgraded to "final" or "settled". */
  confirmed: string;
}

interface Watch extends WatchInit {
  /** Wall clock, and it PERSISTS: a reload resumes the same bound rather than restarting it, so a page
   *  refreshed at 55 s does not buy another minute of spinning. */
  startedAt: number;
}

export type WatchView =
  | { status: "watching"; row: IntentRowWire | null }
  | { status: "confirmed"; row: IntentRowWire }
  | { status: "failed"; row: IntentRowWire; message: string }
  /** WE STOPPED LOOKING. The transaction may well have landed — this is not a failure and must never be
   *  rendered as one. `reference` is the handle support needs. */
  | { status: "unknown"; reference: string; message: string };

/**
 * THE WHOLE DECISION, AS A PURE FUNCTION — no clock of its own, no transport, no toast.
 *
 * ⚠️ THE TERMINAL ROW IS TESTED BEFORE THE BOUND. See the header: a verdict landing on the same tick the
 * bound expires must resolve as that verdict.
 */
export function verdictFor(
  watch: Watch,
  rows: readonly IntentRowWire[],
  now: number,
  boundMs: number,
): WatchView {
  // The signature is a FALLBACK match, for the case the row turns out to be keyed differently than the
  // request id we were handed. A wrong assumption there would present as a trade that never resolves, which
  // is the exact failure this module exists to remove — so it is checked rather than trusted.
  const row =
    rows.find((r) => r.id === watch.id) ??
    (watch.signature === null
      ? undefined
      : rows.find((r) => r.signature !== null && r.signature === watch.signature));

  if (row !== undefined) {
    if (row.status === "confirmed") return { status: "confirmed", row };
    // A BOOLEAN reaches the browser, never the stored message: it is internal and can carry vendor request
    // ids. `abandoned` gets its own sentence because it means something different — nobody ever finished it.
    if (row.status === "failed") return { status: "failed", row, message: FAILED_COPY };
    if (row.status === "abandoned") return { status: "failed", row, message: ABANDONED_COPY };
  }

  if (now - watch.startedAt >= boundMs)
    return { status: "unknown", reference: watch.id, message: BOUND_COPY };

  return { status: "watching", row: row ?? null };
}

/** The two `sessionStorage` methods this uses, so a test can pass a plain object. */
export interface WatchStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface WatcherOptions {
  listIntents: (signal?: AbortSignal) => Promise<IntentsResponse>;
  /** Injected so tests drive it; production passes `lib/time.ts`'s wall clock. A watch's deadline is a
   *  WALL-CLOCK deadline — it is about how long a human has been staring at a spinner, not about chain time. */
  now: () => number;
  toast: (t: Toast) => void;
  pollMs?: number;
  boundMs?: number;
  /** `null` disables persistence — the watch set then dies with the page, which is the old behaviour and is
   *  not what ships. */
  storage?: WatchStorage | null;
  schedule?: (fn: () => void, ms: number) => unknown;
  cancel?: (handle: unknown) => void;
}

export interface IntentWatcher {
  /** Keyed by intent id. A panel subscribes for its own id; the toast needs nobody to be subscribed at all. */
  readonly views: ReadableStore<Readonly<Record<string, WatchView>>>;
  /** Start watching, raise the pending notice, and read IMMEDIATELY — the verdict is usually already durable. */
  watch(init: WatchInit): void;
  /** Rehydrate after a reload. Idempotent. */
  resume(): void;
  /** ONE read + resolve pass over every watch. The timer calls it; tests call it directly, so the whole
   *  machine is reachable without a real clock. */
  pump(): Promise<void>;
  /** How many are in flight. Exposed for the tests and for a future "N confirming" affordance. */
  size(): number;
  dispose(): void;
}

export function createIntentWatcher(opts: WatcherOptions): IntentWatcher {
  const pollMs = opts.pollMs ?? WATCH_POLL_MS;
  const boundMs = opts.boundMs ?? WATCH_BOUND_MS;
  const storage = opts.storage === undefined ? null : opts.storage;
  const schedule = opts.schedule ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const cancel = opts.cancel ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));

  const views = createStore<Readonly<Record<string, WatchView>>>({});
  let watches: readonly Watch[] = [];
  let timer: unknown = null;
  let running = false;
  let kick = false;
  let disposed = false;

  const key = (id: string): string => `order:${id}`;
  const link = (signature: string | null): { href?: string } =>
    signature === null ? {} : { href: `https://solscan.io/tx/${signature}` };

  function persist(): void {
    if (storage === null) return;
    try {
      if (watches.length === 0) storage.removeItem(STORAGE_KEY);
      else storage.setItem(STORAGE_KEY, JSON.stringify(watches.slice(0, MAX_WATCHES)));
    } catch {
      // A full or blocked storage must never break a trade in flight. The watch still runs in memory; it
      // just will not survive a reload, which is the state this whole module improved on.
    }
  }

  function setView(id: string, next: WatchView): void {
    views.set((prev) => {
      const before = prev[id];
      // Reference equality is `createStore`'s whole change test, so an unchanged verdict must return the SAME
      // object — otherwise every 2 s tick wakes every subscriber for a view nobody's value changed.
      if (before !== undefined && same(before, next)) return prev;
      const merged: Record<string, WatchView> = { ...prev, [id]: next };
      const ids = Object.keys(merged);
      if (ids.length <= MAX_VIEWS) return merged;
      for (const stale of ids.slice(0, ids.length - MAX_VIEWS)) delete merged[stale];
      return merged;
    });
  }

  function apply(watch: Watch, view: WatchView): void {
    setView(watch.id, view);
    if (view.status === "watching") return;

    const signature = view.status === "unknown" ? watch.signature : view.row.signature;
    const href = link(signature ?? watch.signature);
    if (view.status === "confirmed")
      opts.toast({
        key: key(watch.id),
        kind: "success",
        text: watch.confirmed,
        ...href,
        ttlMs: RESOLVED_TTL_MS,
      });
    else if (view.status === "failed")
      opts.toast({
        key: key(watch.id),
        kind: "error",
        text: view.message,
        ...href,
        ttlMs: RESOLVED_TTL_MS,
      });
    // ⚠️ `info`, NOT `error`, and no spinner. "We stopped checking" is not "it failed" — see the header.
    else
      opts.toast({
        key: key(watch.id),
        kind: "info",
        text: view.message,
        ...href,
        ttlMs: UNKNOWN_TTL_MS,
      });

    watches = watches.filter((w) => w.id !== watch.id);
    persist();
  }

  function announce(watch: Watch): void {
    opts.toast({
      key: key(watch.id),
      kind: "pending",
      text: watch.pending,
      // THE LINK IS ON THE PENDING NOTICE, not only on the result. The user in the bug report went to solscan
      // by hand while we were holding the signature and rendering a spinner over it.
      ...link(watch.signature),
      ttlMs: null,
    });
    setView(watch.id, { status: "watching", row: null });
  }

  function loop(): void {
    if (disposed || running) return;
    running = true;
    void pump().finally(() => {
      running = false;
      if (disposed) return;
      if (kick) {
        kick = false;
        loop();
        return;
      }
      if (watches.length > 0) timer = schedule(loop, pollMs);
    });
  }

  async function pump(): Promise<void> {
    if (watches.length === 0) return;
    let res: IntentsResponse;
    try {
      res = await opts.listIntents();
    } catch (e) {
      if (isApiError(e) && e.code === "aborted") return;
      if (isSessionExpiry(e)) {
        onUnauthorized();
        // THE TRADE IS UNAFFECTED BY OUR SESSION ENDING — the chain does not care, and neither does the intent
        // row. We simply cannot read it any more, which is `unknown` and not a failure.
        for (const w of [...watches])
          apply(w, { status: "unknown", reference: w.id, message: SIGNED_OUT_COPY });
        return;
      }
      // A TRANSIENT READ FAILURE IS NOT A VERDICT — but the bound still runs while we cannot read, otherwise
      // a server that is down for a minute produces the permanent spinner this module deleted.
      for (const w of [...watches]) apply(w, verdictFor(w, [], opts.now(), boundMs));
      return;
    }
    for (const w of [...watches]) apply(w, verdictFor(w, res.intents, opts.now(), boundMs));
  }

  return {
    views,
    watch(init) {
      if (disposed) return;
      const watch: Watch = { ...init, startedAt: opts.now() };
      watches = [...watches.filter((w) => w.id !== watch.id), watch].slice(-MAX_WATCHES);
      persist();
      announce(watch);
      if (running) kick = true;
      else loop();
    },
    resume() {
      if (disposed || storage === null) return;
      const stored = readStored(storage);
      if (stored.length === 0) return;
      const now = opts.now();
      // ⚠️ A WATCH WHOSE BOUND ALREADY EXPIRED WHILE NOTHING WAS RUNNING IS DROPPED SILENTLY. Re-announcing it
      // only to resolve it to "we stopped checking" would nag on every reload for the rest of the session
      // about a trade whose outcome is already sitting on the wallet page. The record is the record; a toast
      // is a notice about something happening NOW.
      const live = stored.filter((w) => now - w.startedAt < boundMs);
      const known = new Set(watches.map((w) => w.id));
      for (const w of live) {
        if (known.has(w.id)) continue;
        watches = [...watches, w].slice(-MAX_WATCHES);
        announce(w);
      }
      persist();
      if (watches.length > 0 && !running) loop();
    },
    pump,
    size: () => watches.length,
    dispose() {
      disposed = true;
      if (timer !== null) cancel(timer);
      timer = null;
    },
  };
}

/** Two verdicts that say the same thing. Compared on the fields a subscriber can render. */
function same(a: WatchView, b: WatchView): boolean {
  if (a.status !== b.status) return false;
  if (a.status === "watching" && b.status === "watching")
    return (
      (a.row?.id ?? null) === (b.row?.id ?? null) &&
      (a.row?.status ?? null) === (b.row?.status ?? null)
    );
  if (a.status === "unknown" && b.status === "unknown") return a.reference === b.reference;
  if (a.status === "confirmed" && b.status === "confirmed") return a.row.id === b.row.id;
  if (a.status === "failed" && b.status === "failed")
    return a.row.id === b.row.id && a.message === b.message;
  return false;
}

/** Defensive to the last field: this is parsed from storage a previous BUILD wrote, so its shape is not a
 *  compile-time fact. A junk entry is dropped, never rendered. */
function readStored(storage: WatchStorage): Watch[] {
  let raw: string | null = null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return [];
  }
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: Watch[] = [];
  for (const entry of parsed) {
    if (typeof entry !== "object" || entry === null) continue;
    const w = entry as Record<string, unknown>;
    if (typeof w.id !== "string" || w.id.length === 0) continue;
    if (typeof w.pending !== "string" || typeof w.confirmed !== "string") continue;
    if (typeof w.startedAt !== "number" || !Number.isFinite(w.startedAt)) continue;
    out.push({
      id: w.id,
      signature: typeof w.signature === "string" ? w.signature : null,
      pending: w.pending,
      confirmed: w.confirmed,
      startedAt: w.startedAt,
    });
    if (out.length === MAX_WATCHES) break;
  }
  return out;
}

/**
 * THE ONE WATCHER FOR THE APP. Created at module load with the real transport, the real clock and the real
 * toast store, and resumed by the toast HOST (`app/shell/Toasts.tsx`) — the resolver is mounted where the
 * notices it resolves are rendered, so the two cannot come apart again.
 */
export const intentWatcher: IntentWatcher = createIntentWatcher({
  listIntents: api.listIntents,
  now: nowMs,
  toast: pushToast,
  // ⚠️ `sessionStorage`, NOT `localStorage`, and it is the same reasoning as the bearer next door: a watch is
  // scoped to the browsing session that started the trade. A second tab has its own; a closed browser has
  // none, and the wallet page's activity list is the durable record either way.
  storage: globalThis.sessionStorage ?? null,
});

/** Start watching an intent on the app's one watcher. The call site a panel uses. */
export function watchIntent(init: WatchInit): void {
  intentWatcher.watch(init);
}

/** Rehydrate the watch set after a reload. Called by the toast HOST, once, when a session exists. */
export function resumeWatches(): void {
  intentWatcher.resume();
}
